import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

// Connessione WebSocket (una sola per sessione) che riceve gli eventi realtime dal
// backend e li rilancia come eventi window già usati dall'app:
//  - "approvals_changed" -> aggiorna i badge Approvazioni (hook usePendingApprovals)
//  - "notification"      -> aggiorna la campanella notifiche
// Se il WebSocket non è disponibile, resta attivo il polling (fallback) lato client.
const RealtimeBridge = () => {
  const { user } = useAuth();
  const closedRef = useRef(false);
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return undefined;
    closedRef.current = false;
    let attempt = 0;

    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = base.replace(/^http/, "ws") + "/api/ws";

    const scheduleReconnect = () => {
      if (closedRef.current) return;
      attempt += 1;
      const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
      reconnectRef.current = setTimeout(connect, delay);
    };

    function connect() {
      if (closedRef.current) return;
      let ws;
      try { ws = new WebSocket(wsUrl); }
      catch { scheduleReconnect(); return; }
      wsRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        pingRef.current = setInterval(() => {
          try { if (ws.readyState === 1) ws.send("ping"); } catch { /* noop */ }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch { return; }
        if (data?.type === "approvals_changed") window.dispatchEvent(new Event("tb-approvals-changed"));
        else if (data?.type === "notification") window.dispatchEvent(new Event("tb-notifications-changed"));
      };
      ws.onclose = () => { clearInterval(pingRef.current); scheduleReconnect(); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    }

    connect();

    return () => {
      closedRef.current = true;
      clearInterval(pingRef.current);
      clearTimeout(reconnectRef.current);
      try { if (wsRef.current) wsRef.current.close(); } catch { /* noop */ }
    };
  }, [user?.id]);

  return null;
};

export default RealtimeBridge;
